import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { add, isBefore } from 'date-fns';
import { BehaviorSubject, Observable, of, OperatorFunction, pipe, throwError } from 'rxjs';
import { catchError, filter, mapTo, switchMap, take, tap, timeout } from 'rxjs/operators';
import { AuthStoreService } from 'src/app/shared/services/auth/auth-store.service';
import { AnalyticsEvent, AnalyticsService } from '../../core/services/analytics-service';
import { ApiService } from '../../core/services/api.service';
import { DialogService } from '../../core/services/dialog.service';
import { HandsetService } from '../../core/services/handset.service';
import {
  LiabilitiesWithUnknownStatusDialogComponent,
} from '../../liabilities/components/liabilities-with-unknown-status-dialog/liabilities-with-unknown-status-dialog.component';
import { IErrorVM, ImojeCall, IRefuseCreatePaymentLiabilityVM } from '../../liabilities/models/model';
import { AddToCartEventSource } from '../models/add-to-cart-event-source.enum';
import { PaymentMethodCode } from '../models/payment-method-code.enum';
import { PaymentStatus } from '../models/payment-status.model';
import { BasePayment, Payment, PaymentId, PaymentType } from '../models/payment.model';
import { RedirectCode } from '../models/redirect-code.enum';
import { WIDTH_560 } from '../../shared/configs/dialog-config';
import { PaymentErrorEnum } from '../../shared/models/payment-error-enum.enum';
import {
  UnverifiedCustomerDialogComponent,
} from 'src/app/shared-dialogs/unverified-customer-dialog/unverified-customer-dialog.component';
import {
  MixedCartClearanceQuestionDialogComponent,
} from '../../shared-dialogs/mixed-cart-clearance-question-dialog/mixed-cart-clearance-question-dialog.component';
import {
  TopUpCartErrors,
  TopUpErrorDescription,
  TopUpErrorHeader,
  TopUpPaymentError,
} from '../../topup/models/topup-model';
import { LiabilitiesPaymentType } from '../../liabilities/models/liabilities-payment-type.enum';
import { ErrorCode } from '../../core/models/error-code.enum';
import { ForceRecreatePaymentRequest } from '../models/force-recreate-payment-request.model';
import { AppConfig } from '../../core/services/app-config.service';
import { CustomerStoreService } from '../../core/services/customer-store.service';
import { MessageButtons, MessageType } from '../../shared/models/messages.model';
import { MessagesStoreService } from '../../shared/services/messages-store.service';

const getMultipayoutIndex = (key: string) => parseInt(key.split('[')[1].split(']')[0], 10);
const paymentCreditCardTimeout = 60000;

const getSortedFormParams = (arr) =>
  arr.sort((prevEntry, nextEntry) => {
    const [prevKey] = prevEntry;
    const [nextKey] = nextEntry;
    if (prevKey.includes('multipayout') && nextKey.includes('multipayout')) {
      const prevIndex = getMultipayoutIndex(prevKey);
      const nextIndex = getMultipayoutIndex(nextKey);
      if (prevIndex === nextIndex) {
        return prevKey < nextKey ? -1 : 1;
      }
      return prevIndex - nextIndex;
    }
    return prevKey < nextKey ? -1 : 1;
  });

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  public basePayment$: Observable<BasePayment>;
  private refresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    private api: ApiService,
    private analytics: AnalyticsService,
    private router: Router,
    private handsetService: HandsetService,
    private dialog: DialogService,
    private authStoreService: AuthStoreService,
    private appConfig: AppConfig,
    private customerStoreService: CustomerStoreService,
    private messagesStoreService: MessagesStoreService,
  ) {
    this.basePayment$ = this.refresh.pipe(
      switchMap(() => {
        if (this.authStoreService.isAuthenticated) {
          return this.api.get<BasePayment>(`/payments/uncompleted`, null, false, 'basePayment', ([data, socketFrame]) =>
            socketFrame ? socketFrame : data,
          );
        } else {
          return of(null);
        }
      }),
    );
  }

  private static redirectToImoje(imojeAction: IImojeAction) {
    window.location.href = imojeAction.url;
  }

  public refreshBasePayment(): void {
    this.refresh.next(!this.refresh.value);
  }

  public getPayment(paymentId: string, subscribe?: boolean): Observable<Payment> {
    return this.api.get<Payment>(`/payments/${paymentId}`, null, subscribe, 'payment', ([data, socketFrame]) =>
      socketFrame ? socketFrame : data,
    );
  }

  public getStartedPayment(): Observable<BasePayment> {
    return this.api.get<BasePayment>('/payments/findStartedPayment');
  }

  public getCreatedPaymentId(): Observable<string | null> {
    return this.api.get<string | null>('/payments/lastCreated');
  }

  public findPaymentIdByLiabilityId(liabilityId: string): Observable<PaymentId> {
    return this.api.get<PaymentId>(`/payments/findIdByLiabilityId/${liabilityId}`);
  }

  public redirectToCurrentPayment(redirectUrl: string = '/liabilities/pay/'): Observable<any> {
    return this.api.get('/payments/lastCreated').pipe(
      tap((response) => {
        if (response) {
          this.router.navigateByUrl(`${redirectUrl}${response}`);
        } else {
          this.router.navigate(['liabilities']);
        }
      }),
    );
  }

  public createCartSuggestionPayment(token: string): Observable<string> {
    return this.api.post<string>('/payments/cart-suggestion', token);
  }

  public initPayment(liabilityIds: string[], eventSource: AddToCartEventSource, returnUrl?: string): Observable<string> {
    return this.addLiabilityToCart(liabilityIds)
      .pipe(
        // tap(() => {
        //   this.cartStoreService.removeList(liabilityIds);
        // }),
        this.navigateToCreatedCart(eventSource, returnUrl),
        catchError((error: any) => {
          if (error === ErrorCode.READ_ONLY) {
            return of(ErrorCode.READ_ONLY);
          } else {
            var code = error?.error?.errorCode;
            if (code === ErrorCode.INVALID_LIABILITIES_PAYMENT_TYPE) {
              return this.handleCartLiabilitiesPaymentTypeMismatch(liabilityIds, eventSource, returnUrl);
            } else if (code === ErrorCode.INVALID_LIABILITIES_PAYMENT_TYPES
              || code === 'TOP_UP_CANNOT_BE_MIXED_WITH_STANDARD_LIABILITIES') {
              this.messagesStoreService.setState({
                type: MessageType.ERROR,
                buttons: MessageButtons.CLOSE,
                title: 'Niepowodzenie',
                msg: 'Nie można łączyć doładowań z innymi wydatkami.',
                closeable: true,
              });
              return of('');
            } else if (code === ErrorCode.TOP_UP_CANNOT_BE_EXECUTED_FOR_PAYMENT) {
              this.messagesStoreService.setState({
                type: MessageType.ERROR,
                buttons: MessageButtons.CLOSE,
                title: 'Błąd',
                msg: 'Opłacenie doładowania nie jest możliwe.',
                closeable: true,
              });
              return of('');
            } else if (code === ErrorCode.PAYMENT_ALREADY_STARTED) {
              this.messagesStoreService.setState({
                type: MessageType.WARNING,
                buttons: MessageButtons.CUSTOM,
                title: 'Masz już rozpoczętą płatność za inne opłaty',
                msg: 'Aby móc dodać opłatę do koszyka, najpierw dokończ poprzednio rozpoczętą płatność.',
                customButtons: [
                  {
                    label: 'Dokończ płatność',
                    isAccent: true,
                    onClick: this.navigateToPayment.bind(this),
                  },
                  {
                    label: 'Zamknij',
                    isAccent: false,
                    onClick: () => {
                    },
                  },
                ],
              });


              return of('');
            } else {
              return this.handleInitPaymentError(liabilityIds, eventSource, returnUrl, error);
            }
          }
        }),
      );
  }

  private close() {
  }

  private navigateToPayment(): Promise<boolean> {
    return this.getStartedPayment().pipe(
      switchMap(value => this.router.navigate([`/liabilities/pay/${value.id}`])),
    ).toPromise();
  }

  private handleCartLiabilitiesPaymentTypeMismatch(
    liabilityIds: string[],
    eventSource: AddToCartEventSource,
    returnUrl: string,
  ): Observable<any> {
    const dialogRef = this.dialog.open(MixedCartClearanceQuestionDialogComponent, {
      data: {
        cartLiabilitiesPaymentType: LiabilitiesPaymentType.TOP_UP,
      },
    });
    return dialogRef.afterClosed().pipe(
      filter((answer: boolean) => answer),
      switchMap(() => {
        const forceRecreatePaymentData = {
          liabilityIds: liabilityIds,
        };
        return this.forceRecreatePayment(forceRecreatePaymentData);
      }),
      // this.navigateToCreatedCart(returnUrl, eventSource),
      catchError((err: HttpErrorResponse) => this.handleInitPaymentError(liabilityIds, eventSource, returnUrl, err)),
    );
  }

  private navigateToCreatedCart(
    eventSource: AddToCartEventSource,
    returnUrl?: string,
  ): OperatorFunction<string, Payment> {
    return pipe(
      switchMap((forceCartResponse: string) => {
        this.refreshBasePayment();
        if (returnUrl !== undefined) {
          this.router.navigate(['/liabilities/pay', forceCartResponse], {
            queryParams: {
              returnUrl: returnUrl,
            },
          });
        }
        return this.getPayment(forceCartResponse);
      }),
      tap((payment: Payment) => {
        payment.liabilitiesPaymentType === LiabilitiesPaymentType.TOP_UP
          ? this.analytics.trackEvent(new AnalyticsEvent('Add to cart top up', eventSource))
          : this.analytics.trackEvent(new AnalyticsEvent('Add to cart', eventSource));
      }),
    );
  }

  private addLiabilityToCart(liabilityIds: string[]): Observable<string> {
    return this.api.post<string>('/payments', { liabilityIds });
    // .pipe(
    //   tap(() => this.cartLiabilitiesStoreService.removeList(liabilityIds)),
    // );
  }

  public addAllLiabilitiesToCart(): Observable<string> {
    return this.api.post<string>('/payments/addAll', {})
      // .pipe(
      //   tap(() => this.cartLiabilitiesStoreService.clearCheckedLiabilities()),
      // );
      ;
  }

  private forceRecreatePayment(data: ForceRecreatePaymentRequest): Observable<string> {
    return this.api.post<string>('/payments/force-recreate-current-payment', data);
  }

  private handleInitPaymentError(
    liabilityIds: string[],
    eventSource: AddToCartEventSource,
    returnUrl: string,
    err: HttpErrorResponse,
  ) {
    return this.handleError(err, this.initPayment(liabilityIds, eventSource, returnUrl));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private handleCreatePaymentWithCouponError(err: HttpErrorResponse, couponId: string) {
    // return this.handleError(err, this.createPaymentWithCoupon(couponId), couponId);
  }

  private handleError(
    err: HttpErrorResponse,
    actionAfterForceNotPaidLiabilities: Observable<string>,
    couponId: string = null,
  ) {
    const errorVM: IErrorVM = err.error;
    const errorCode = errorVM?.errorCode;
    if ([406, 409, 422].includes(err.status) && (TopUpCartErrors[errorCode] || TopUpPaymentError[errorCode])) {
      this.messagesStoreService.setState({
        type: MessageType.ERROR,
        buttons: MessageButtons.CLOSE,
        title: TopUpErrorHeader[errorCode],
        msg: TopUpErrorDescription[errorCode],
        closeable: true,
      });
    }
    if (errorCode === PaymentErrorEnum.LIABILITIES_WITH_UNKNOWN_STATUS) {
      const unknownStatusLiabilities: IRefuseCreatePaymentLiabilityVM[] = errorVM.errorData.liabilities;
      return this.dialog
        .open(LiabilitiesWithUnknownStatusDialogComponent, {
          data: errorVM.errorData,
          width: WIDTH_560,
          maxWidth: this.handsetService.isXs ? '100vw' : '90vw',
        })
        .afterClosed()
        .pipe(
          filter((accepted) => accepted),
          switchMap(() => this.forceNotPaidLiabilities(unknownStatusLiabilities)),
          switchMap(() => actionAfterForceNotPaidLiabilities),
        );
    } else if (errorCode === PaymentErrorEnum.CUSTOMER_CART_LIMIT_REACHED) {
      this.showUnverifiedCustomerDialog(actionAfterForceNotPaidLiabilities);
    } else if (couponId && errorCode === PaymentErrorEnum.NO_SUGGESTED_LIABILITY_TO_PAY) {
      return this.router.navigate(['/liabilities/pay/'], {
        queryParams: { emptyCartCouponId: couponId },
      });
    } else {
      return of(ErrorCode.READ_ONLY);
    }
  }

  private forceNotPaidLiabilities(unknownStatusLiabilities: IRefuseCreatePaymentLiabilityVM[]) {
    return this.api.post('/liabilities/status/force-not-paid', {
      liabilityIds: unknownStatusLiabilities.map((v) => v.liabilityId),
    });
  }

  private showUnverifiedCustomerDialog(actionAfterForceNotPaidLiabilities$: Observable<String>) {
    this.dialog
      .open(UnverifiedCustomerDialogComponent, {
        maxWidth: WIDTH_560,
        data: {
          action: actionAfterForceNotPaidLiabilities$,
        },
      })
      .afterClosed()
      .subscribe();
  }

  public startPayment(
    paymentId: string,
    method: PaymentMethodCode,
    totalAmount: number,
    paymentType: PaymentType,
  ): Observable<any> {
    this.analytics.trackEvent(
      new AnalyticsEvent('Createnewpayment', {
        amount: totalAmount,
        method,
      }),
    );
    //todo to remove seon and payment
    return this.api.postPayment(`/payments/${paymentId}`, {
      method: method,
      type: paymentType,
      seonSession: paymentId,
      paymentId: paymentId
    }).pipe(
      catchError((error: Error | ErrorCode) => {
        if (error === ErrorCode.READ_ONLY) {
          return throwError(ErrorCode.READ_ONLY);
        } else if (error instanceof HttpErrorResponse) {
          return throwError(error.error);
        }
      }),
    );
  }

  public startCreditCardPayment(
    paymentId: string,
    creditCardId: string,
    totalAmount: number,
    paymentType: PaymentType,
  ): Observable<unknown> {
    this.analytics.trackEvent(
      new AnalyticsEvent('Startcreditcardpayment', {
        amount: totalAmount,
      }),
    );
    return this.api.post(`/payments/${paymentId}/credit-cards/${creditCardId}`, {
      type: paymentType,
    });
  }

  public recreatePayment(paymentId: string, options: IRecreatePaymentOptions = {}): Observable<string> {
    return this.api.post<string>(`/payments/${paymentId}/recreate`, options)
      .pipe(
        tap(() => this.refreshBasePayment()),
        tap(() => {
          // if (options.removedLiabilityIds) {
          //   this.cartLiabilitiesStoreService.removingLiabilityFromCart$.next(options.removedLiabilityIds);
          // }
        }),
      )
      ;
  }

  public cancelPayment(paymentId: string): Observable<void> {
    this.analytics.trackEvent(new AnalyticsEvent('Cancelpayment'));
    return this.api.post(`/payments/${paymentId}/cancel`);
  }

  public paymentWithCreditCard(payment: Payment, creditCardId: string): Observable<any> {
    const payload = {
      redirectCode: 'LIABILITIES',
    };
    if (creditCardId === null) {
      return this.newCreditCard(payment, payload);
    } else {
      return this.existingCreditCard(payment, creditCardId);
    }
  }

  public newCreditCard(payment: Payment, payload: any): Observable<any> {
    return this.api.get<ImojeCall>(`/payments/${payment.id}/newCreditCard`, payload).pipe(
      tap((imojeCall) => this.appendImojeScript(imojeCall)),
      switchMap(undefined),
    );
  }

  public existingCreditCard(payment: Payment, creditCardId: string): Observable<any> {
    return this.api
      .get<any>(`/payments/${payment.id}/existingCreditCard/${creditCardId}`, null, null, null, null, false)
      .pipe(timeout(paymentCreditCardTimeout));
  }

  public initAddNewCard(returnPath: string, liabilitiesPaymentType: LiabilitiesPaymentType): Observable<any> {
    if (liabilitiesPaymentType === null && !this.appConfig.config.topUp.displayTopUpCreditCards) {
      // obejście na rzadki błąd. W momencie gdyby zostały włączone karty dla doładowań TOP_UP
      // (szanse na to są praktycznie zerowe) należy to odkomentować i koszernie poprawić
      liabilitiesPaymentType = LiabilitiesPaymentType.STANDARD;
    }

    const payload = {
      queryParams: `returnPath=${returnPath}`,
      liabilitiesPaymentType,
      redirectCode: 'CREDIT_CARDS',
    };
    return this.api.get<ImojeCall>(`/payments/registerNewCard`, payload).pipe(
      tap((imojeCall) => this.appendImojeScript(imojeCall)),
      mapTo(undefined),
    );
  }

  public initAddNewCardForBilltechPayPayment(
    returnPath: string,
    paymentId: string,
    liabilitiesPaymentType: LiabilitiesPaymentType,
  ): Observable<any> {
    if (liabilitiesPaymentType === null && !this.appConfig.config.topUp.displayTopUpCreditCards) {
      // obejście na rzadki błąd. W momencie gdyby zostały włączone karty dla doładowań TOP_UP
      // (szanse na to są praktycznie zerowe) należy to odkomentować i koszernie poprawić
      liabilitiesPaymentType = LiabilitiesPaymentType.STANDARD;
    }

    const payload = {
      paymentId,
      queryParams: `returnPath=${returnPath}`,
      liabilitiesPaymentType,
      redirectCode: 'BILLTECH_PAY_CREDIT_CARDS',
    };
    return this.api.get<ImojeCall>(`/payments/registerNewCard`, payload).pipe(
      tap((imojeCall) => this.appendImojeScript(imojeCall)),
      mapTo(undefined),
    );
  }

  public paywallPayment(payment: Payment, redirectCode: RedirectCode): Observable<any> {
    const payload = {
      redirectCode,
    };
    return this.api.post<ImojeCall>(`/payments/${payment.id}/imojeCall`, payload).pipe(
      tap((imojeCall) => this.submitImojeCall(imojeCall)),
      mapTo(undefined),
    );
  }

  public twistoPayment(paymentId: string, redirectCode: RedirectCode): Observable<any> {
    const payload = {
      redirectCode,
    };
    return this.api.post<ImojeCall>(`/payments/${paymentId}/imojeCall`, payload).pipe(
      tap((imojeCall) => this.submitImojeCall(imojeCall)),
      mapTo(undefined),
    );
  }

// eslint-disable-next-line @typescript-eslint/no-unused-vars
  public deferPaymentWithPayka(payment: Payment, redirectCode: RedirectCode): Observable<any> {
    let paymentId = payment.id;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
    let { customerId } = this.authStoreService;
    return this.customerStoreService.getAccountStatus()
      .pipe(
// eslint-disable-next-line @typescript-eslint/no-unused-vars
        switchMap((status) => this.router.navigateByUrl(`/liabilities/pay/${paymentId}/defer`)),
        take(1),
        //todo do ogarnicia obsluga bledow
        // catchError((err: HttpErrorResponse) => this.handleCreatePaymentWithCouponError(err)),
      );
  }

  public transactionApiCall(payment: Payment, redirectCode: RedirectCode): Observable<any> {
    const payload = {
      redirectCode,
    };
    return this.api.get<IImojeAction>(`/payments/${payment.id}/transaction`, payload).pipe(
      tap((imojeAction) =>
        imojeAction.method === 'GET'
          ? PaymentService.redirectToImoje(imojeAction)
          : this.submitImojeActionCall(imojeAction),
      ),
      mapTo(undefined),
    );
  }

  public submitImojeCall(imojeCall: ImojeCall): void {
    const form = document.createElement('form');
    const formParams = getSortedFormParams(Object.entries(imojeCall.paymentRequestParams));

    form.setAttribute('method', 'POST');
    form.setAttribute('action', imojeCall.apiUrl);

    for (const [key, value] of formParams) {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', key);
      input.setAttribute('value', value);
      form.appendChild(input);
    }

    this.trackPaymentEvent(imojeCall.paymentRequestParams.amount);

    document.body.appendChild(form);
    form.submit();
  }

  public appendImojeScript(imojeCall: ImojeCall): void {
    const script = document.createElement('script');
    script.src = imojeCall.apiUrl;
    script.id = 'imoje-widget__script';
    script.setAttribute('data-inline', 'true');
    for (const [key, value] of Object.entries(imojeCall.paymentRequestParams)) {
      if (key === 'amount') {
        this.trackPaymentEvent(value);
      }
      script.setAttribute(key, value);
    }
    document.getElementsByTagName('head')[0].appendChild(script);
  }

  public submitImojeActionCall(imojeAction: IImojeAction): void {
    const form = document.createElement('form');
    form.setAttribute('method', 'POST');
    form.setAttribute('action', imojeAction.url);

    const params = decodeURIComponent(imojeAction.contentBodyRaw).split('&');
    params.forEach((str) => {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      const keyValuePair = str.replace(/\+/g, ' ').split('=');
      input.setAttribute('name', keyValuePair[0]);
      input.setAttribute('value', keyValuePair[1]);
      if (keyValuePair[0] === 'amount') {
        this.trackPaymentEvent(keyValuePair[1]);
      }
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }


  private trackPaymentEvent(value: any): void {
    this.analytics.trackPayment(Number(value) / 100);
  }

  public showCloseDueDateInfo(payment: Payment): boolean {
    var list: PaymentStatus[] = [PaymentStatus.CREATED, PaymentStatus.STARTED, PaymentStatus.COMPLETED];

    if (list.includes(payment.status)) {
      return (
        payment.paymentFragments
          .map((pf) => pf.liability)
          .filter((l) => isBefore(new Date(l.paymentDue), add(new Date(), { days: 5 }))).length > 0
      );
    }
  }

  public getBasePaymentByPayByLinkToken(payByLinkToken: string): Observable<BasePayment> {
    return this.api.get<BasePayment>(`/payments/pay-by-link-payment/${payByLinkToken}`);
  }

  public removeCoupon(paymentId: string): Observable<string> {
    return this.api.delete(`/payments/${paymentId}/remove-coupon`);
  }

  public changeCoupon(paymentId: string, couponId: string): Observable<string> {
    return this.api.put(`/payments/${paymentId}/assign-coupon/${couponId}`);
  }

  public createPaymentWithCoupon(couponId: string): Observable<string | boolean> {
    return this.api
      .post<string>(`/payments/payment-with-coupon`, { couponId })
      .pipe(
        tap(() => this.refreshBasePayment()),
        switchMap((newPaymentId) => this.router.navigate(['/liabilities/pay', newPaymentId])),
        take(1),
        // todo
        catchError((err: HttpErrorResponse) => of(err.message)),
        // catchError((err: HttpErrorResponse) => this.handleCreatePaymentWithCouponError(err, couponId)),
      );
  }

  public createPaymentForCustomerBiller(customerBillerId: string, couponId?: string): Observable<any> {
    return this.api
      .post<string>(`/payments/customer-biller/${customerBillerId}`, { couponId })
      .pipe(
        tap(() => this.refreshBasePayment()),
        switchMap((newPaymentId) => this.router.navigate(['/liabilities/pay', newPaymentId])),
        take(1),
      );
  }

  public clear() {
    this.basePayment$ = of(null);
  }

}

interface IImojeAction {
  type: string;
  url: string;
  method: string;
  contentType: string;
  contentBodyRaw: string;
}

interface IRecreatePaymentOptions {
  removedLiabilityIds?: string[];
  addedLiabilityIds?: string[];
}
